import { Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';

export interface ResetAllDemoPatientsDialogProps {
  open: boolean;
  patientCount: number;
  isPending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

const TITLE_ID = 'reset-all-demo-patients-dialog-title';
const DESCRIPTION_ID = 'reset-all-demo-patients-dialog-description';

export function ResetAllDemoPatientsDialog({
  open,
  patientCount,
  isPending,
  onConfirm,
  onOpenChange,
}: ResetAllDemoPatientsDialogProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (isPending && !nextOpen) {
      return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        showCloseButton={!isPending}
        className="border-red-500/60 bg-red-950/90 text-red-100 shadow-[0_0_40px_rgba(239,68,68,0.15)]"
      >
        <DialogHeader>
          <div className="flex items-start gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-500/20">
              <TriangleAlert className="size-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="space-y-1.5">
              <DialogTitle id={TITLE_ID} className="text-red-50">
                Reset all {patientCount} demo patients to base?
              </DialogTitle>
              <DialogDescription id={DESCRIPTION_ID} className="text-red-300/80">
                This discards every conversation, care-state change, note, draft, lab and
                prescription record on all of your demo patients, restores each one to base, and
                brings back the onboarding tips you have dismissed. Only your copies are affected;
                no other clinician sees these patients. They come back one at a time, which can take
                a few minutes.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="border-red-500/30 bg-red-900/60 text-red-200 hover:bg-red-800/60"
          >
            Keep as is
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            aria-busy={isPending}
            className="bg-red-600 text-white hover:bg-red-500"
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Resetting…
              </>
            ) : (
              'Reset all'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
